"use client";

import { Fragment, useMemo } from "react";
import { useSearchParams } from "next/navigation";
import { CategoryCard } from "@/components/category-card";
import type { Category } from "@/lib/types";

interface CategoriesContentProps {
  categories: Category[];
  brandCounts?: Record<string, number>;
}

type SortOption = "name-asc" | "name-desc" | "brands-desc" | "brands-asc";

export function CategoriesContent({ categories, brandCounts = {} }: CategoriesContentProps) {
  const searchParams = useSearchParams();
  const searchQuery = searchParams.get("q") || "";
  const sortBy = (searchParams.get("sort") || "name-asc") as SortOption;

  // Filter and sort categories
  const filteredAndSortedCategories = useMemo(() => {
    let filtered = categories.filter(
      (category) => category && category.name && category.slug
    );

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (category) =>
          category.name.toLowerCase().includes(query) ||
          category.description?.toLowerCase().includes(query) ||
          category.seoDescription?.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case "name-asc":
          return a.name.localeCompare(b.name);
        case "name-desc":
          return b.name.localeCompare(a.name);
        case "brands-desc":
          return (brandCounts[b.slug] ?? b.brandSlugs?.length ?? 0) - (brandCounts[a.slug] ?? a.brandSlugs?.length ?? 0);
        case "brands-asc":
          return (brandCounts[a.slug] ?? a.brandSlugs?.length ?? 0) - (brandCounts[b.slug] ?? b.brandSlugs?.length ?? 0);
        default:
          return 0;
      }
    });

    return sorted;
  }, [categories, brandCounts, searchQuery, sortBy]);

  return (
    <div className="py-8 md:py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <header className="mb-12">
          <h1 className="text-3xl md:text-4xl font-serif font-semibold text-foreground">
            Browse by Category
          </h1>
          <p className="mt-4 text-lg text-muted-foreground leading-relaxed max-w-3xl">
            Explore brand ownership across different industries. Select a
            category to discover which companies own the brands in that sector.
          </p>
        </header>

        {/* Search */}
        <div className="mb-12 max-w-xl">
          <input
            type="text"
            placeholder="Search categories..."
            defaultValue={searchQuery}
            onChange={(e) => {
              const params = new URLSearchParams();
              if (e.target.value) {
                params.set("q", e.target.value);
              }
              if (sortBy !== "name-asc") {
                params.set("sort", sortBy);
              }
              window.location.href = `/categories?${params.toString()}`;
            }}
            className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>

        {/* Sort */}
        <div className="mb-8 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-foreground">
              Sort by
            </label>
            <select
              value={sortBy}
              onChange={(e) => {
                const params = new URLSearchParams();
                if (searchQuery) {
                  params.set("q", searchQuery);
                }
                if (e.target.value !== "name-asc") {
                  params.set("sort", e.target.value);
                }
                window.location.href = `/categories?${params.toString()}`;
              }}
              className="px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm"
            >
              <option value="name-asc">Name (A-Z)</option>
              <option value="name-desc">Name (Z-A)</option>
              <option value="brands-desc">Most Brands</option>
              <option value="brands-asc">Fewest Brands</option>
            </select>
          </div>

          {/* Results count */}
          <div className="text-sm text-muted-foreground ml-auto">
            Showing {filteredAndSortedCategories.length} of {categories.length}{" "}
            categories
          </div>
        </div>

        {/* Categories Grid */}
        {filteredAndSortedCategories.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {filteredAndSortedCategories.map((category) => (
              <CategoryCard
                key={category.slug}
                category={category}
                brandCount={brandCounts[category.slug]}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-lg text-muted-foreground">
              No categories found matching your search.
            </p>
          </div>
        )}

        {/* Info Section */}
        <div className="mt-16 p-8 bg-muted rounded-lg border border-border">
          <h2 className="text-xl font-serif font-semibold text-foreground mb-4">
            Why Browse by Category?
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            Browsing brands by category helps you understand the competitive
            landscape within specific industries. You can discover how
            conglomerates dominate certain sectors, which companies own
            competing brands, and how corporate ownership shapes the products
            available in each market segment. This knowledge empowers consumers
            to make more informed purchasing decisions.
          </p>
        </div>
      </div>
    </div>
  );
}
